import { Component, OnInit } from '@angular/core'
import { AlertController, LoadingController, ModalController, NavController } from '@ionic/angular/standalone'
import { Observable, map, tap } from 'rxjs'
import { Clipboard } from '@capacitor/clipboard'
import { MessageService } from 'primeng/api'
import { entityCode } from 'src/app/shared/entity-code'
import { CacheManagementService } from 'src/app/services/cache-management.service'
import { AuthService } from 'src/app/core/auth.service'
import { MessageData, MessageGroupsModel, MessageModel, MessageType, visibleReceivedMessages } from 'src/app/models/message.model'
import { Profile } from 'src/app/models/profile.model'
import { DatastoreCollectionsService } from 'src/app/services/datastore-collections.service'
import { DatastoreEntityService } from 'src/app/services/datastore-entity.service'
import { DatastoreMessagesService } from 'src/app/services/datastore-messages.service'
import { FirednaProfileService } from 'src/app/services/firedna-profile.service'
import { config, environment } from 'src/environments/environment'
import { ConsoleLogPage } from '../console-log/console-log.page'
import { DebugViewPage } from '../debug-view/debug-view.page'
import { NavigationExtras } from '@angular/router'
import { NetworkStatusService } from 'src/app/services/network-status.service'
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog'
import { openWhatsNew } from '../whats-new/whats-new.component'

/**
 * The avatar menu: who you are, your company code, Profile/Company details, messages and
 * account actions. Opened from the side menu via DialogService (full-bleed, own header).
 */
@Component({
  selector: 'app-profile-overview',
  templateUrl: './profile-overview.page.html',
  styleUrls: ['./profile-overview.page.scss'],
  standalone: false,
})
export class ProfileOverviewPage implements OnInit {
  public profile$: Observable<Profile>
  public profile: Profile
  mediaUrl: string = config.mediaUrl
  public messages$: Observable<MessageGroupsModel>
  MessageType = MessageType
  currentApplicationVersion = environment.appVersion
  cacheSizeBytes: number = 0
  cacheSizeMB: number = 0
  uuidFoldersToDelete: string[] = []
  acceptingAllShares = false
  /** The user's company and its short code (shared so others can send them job schedules). */
  company$: Observable<{ name: string; code: string } | null>

  constructor(
    private ref: DynamicDialogRef,
    // Console log / debug view are still Ionic modals
    private modalController: ModalController,
    private datastoreMessagesService: DatastoreMessagesService,
    public alertController: AlertController,
    private datastoreCollectionsService: DatastoreCollectionsService,
    private networkStatusService: NetworkStatusService,
    public auth: AuthService,
    private profileService: FirednaProfileService,
    private navCtrl: NavController,
    private datastoreEntityService: DatastoreEntityService,
    private loadingController: LoadingController,
    private cacheManagementService: CacheManagementService,
    private messageService: MessageService,
    private dialogService: DialogService
  ) {}

  ngOnInit() {
    console.log('ProfileOverviewPage', environment.appVersion.split('-')[0])
    this.profile$ = this.profileService.profileData$.pipe(tap((profile) => (this.profile = profile)))
    this.messages$ = this.datastoreMessagesService.messages$
    this.company$ = this.datastoreEntityService.entity$.pipe(
      map((e: any) => (e?.id ? { name: e.name ?? '', code: entityCode(e.name, e.id) } : null))
    )
    this.calculateCacheSize()
  }

  async goToReportRequest(message: MessageModel) {
    await this.datastoreMessagesService.actionMessage(message)

    if (message.msgType === MessageType.ReportRequest) {
      const reportRequestData: MessageData = message.data as MessageData

      const navigationExtras: NavigationExtras = {
        queryParams: {
          collection: reportRequestData.collectionId,
        },
      }
      this.navCtrl.navigateForward(['/app/collection-manager/collection-info'], navigationExtras)
    } else {
      this.navCtrl.navigateForward(['/app/generated-reports'])
    }
    this.dismiss()
  }

  async copyCompanyCode(code: string) {
    try {
      await Clipboard.write({ string: code })
      this.messageService.add({ severity: 'success', summary: 'Company code copied', detail: code })
    } catch (err) {
      console.error(err)
      this.messageService.add({ severity: 'error', summary: 'Could not copy — select the code and copy it manually' })
    }
  }

  /** Job schedule alert: acknowledge it and open the schedule. */
  async openJobSchedule(message: MessageModel) {
    await this.datastoreMessagesService.actionMessage(message, true)
    this.datastoreMessagesService.loadMessages()
    if (message.data?.worklistId) {
      this.navCtrl.navigateForward(['/app/job-schedules', message.data.worklistId])
    }
    this.dismiss()
  }

  receivedMessages(messages: MessageGroupsModel): MessageModel[] {
    return visibleReceivedMessages(messages.messagesTo, this.hasRole('messageService'))
  }

  async processMessage(message: MessageModel) {
    await this.datastoreMessagesService.actionMessage(message)
    this.datastoreMessagesService.loadMessages()
  }

  pendingShareRequests(messages: MessageGroupsModel): MessageModel[] {
    return (messages.messagesTo ?? []).filter((m) => m.msgType === MessageType.ShareRequest && m.status !== 'Complete')
  }

  async acceptAllShareRequests(messages: MessageGroupsModel) {
    const pending = this.pendingShareRequests(messages)
    if (this.acceptingAllShares || pending.length === 0) return
    this.acceptingAllShares = true
    const loading = await this.loadingController.create({
      message: `Accepting ${pending.length} share requests...`,
    })
    await loading.present()
    try {
      let done = 0
      for (const message of pending) {
        await this.datastoreMessagesService.actionMessage(message, true)
        done++
        loading.message = `Accepted ${done} of ${pending.length}...`
      }
    } finally {
      loading.dismiss()
      this.acceptingAllShares = false
      this.datastoreMessagesService.loadMessages()
    }
  }
  trashMessage(message: MessageModel) {
    this.datastoreMessagesService.deleteMessage(message)
  }
  logOut() {
    this.dismiss()
    this.datastoreCollectionsService.cancelCollectionCacheBuild()
    this.auth.logoutUser()
  }
  openProfileSection(section: 'profile' | 'company') {
    this.dismiss()
    this.navCtrl.navigateForward(['/app/company-profile-details'], { queryParams: { section } })
  }

  async resetPassword() {
    const alert = await this.alertController.create({
      header: 'Password reset',
      message: 'You will receive an email with instructions.',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {
            console.log('Confirm Cancel: blah')
          },
        },
        {
          text: 'Okay',
          handler: () => {
            console.log('Confirm Okay')
            this.auth.resetPassword(this.profile.email)
            this.dismiss()
          },
        },
      ],
    })

    await alert.present()
  }
  async viewLog() {
    this.dismiss()
    const modal = await this.modalController.create({
      component: ConsoleLogPage,
    })
    return await modal.present()
  }
  async viewDebug() {
    this.dismiss()
    const modal = await this.modalController.create({
      component: DebugViewPage,
    })
    return await modal.present()
  }
  whatsNew() {
    this.dismiss()
    openWhatsNew(this.dialogService)
  }
  setAppOffline() {
    this.networkStatusService.toggleConnected()
    this.dismiss()
  }
  hasRole(role: string) {
    if (this.auth.hasRole(role)) {
      return true
    } else {
      return false
    }
  }

  async calculateCacheSize() {
    try {
      const stats = await this.cacheManagementService.getCacheStats()
      this.cacheSizeBytes = stats.totalSizeBytes
      this.cacheSizeMB = stats.totalSizeMB
      this.uuidFoldersToDelete = stats.uuidFolders
      console.log(`Cache size: ${this.cacheSizeMB} MB, UUID folders: ${this.uuidFoldersToDelete.length}`)
    } catch (error) {
      console.error('Error calculating cache size:', error)
      this.cacheSizeBytes = 0
      this.cacheSizeMB = 0
      this.uuidFoldersToDelete = []
    }
  }

  async clearCacheUuidFolders() {
    if (this.uuidFoldersToDelete.length === 0) {
      const alert = await this.alertController.create({
        header: 'No Cache to Clear',
        message: 'No cache found to clear currently',
        buttons: ['OK'],
      })
      await alert.present()
      return
    }

    const alert = await this.alertController.create({
      header: 'Clear Cache?',
      message: `Ensure any offline data & images are synced before proceeding`,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          cssClass: 'secondary',
        },
        {
          text: 'Delete',
          handler: async () => {
            await this.deleteUuidFoldersAction()
          },
        },
      ],
    })

    await alert.present()
  }

  async deleteUuidFoldersAction() {
    const loading = await this.loadingController.create({
      cssClass: 'my-custom-class',
      message: 'Clearing cache...',
      duration: 60000,
    })
    await loading.present()

    const result = await this.cacheManagementService.deleteUuidFolders(this.uuidFoldersToDelete, (deleted, total) => {
      loading.message = `Cleared ${deleted}/${total} folders...`
    })

    loading.dismiss()

    const successAlert = await this.alertController.create({
      header: 'Cache Cleared',
      message: `Deleted ${result.totalDeleted} folders. Failed: ${result.totalFailed}`,
      buttons: ['OK'],
    })
    await successAlert.present()

    // Recalculate cache size
    await this.calculateCacheSize()
  }

  dismiss() {
    this.ref.close()
  }

  initials(name: string | undefined): string {
    const parts = (name ?? '').trim().split(/\s+/).filter(Boolean)
    return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase()
  }

  unreadCount(messages: MessageGroupsModel): number {
    return this.receivedMessages(messages).filter((m) => !m.msgRead).length
  }
}
